/*
 * Copyright 2026 The Android Open Source Project
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     https://www.apache.org/licenses/LICENSE-2.0
 */
package recommendations

import i18n.StringKey
import i18n.tr
import kotlin.math.abs
import kotlin.math.roundToInt

/**
 * The ranked pool of SECONDARY dashboard insights — everything the
 * Recommendation widget can say once the primary line has been decided.
 *
 * ## Division of labour with [computeSavingsRecommendationCardState]
 *
 * [computeSavingsRecommendationCardState] stays the single authority for the
 * PRIMARY insight: it encodes the autopay-before-payday rule (don't scold a
 * user for being "over budget" when their income simply hasn't landed yet).
 * This object never re-decides that. It only fills the #2 and #3 slots a taller
 * widget has room for, and it is told what the primary already says via
 * [Primary] so it can refuse to restate it.
 *
 * ## Why it lives in shared
 *
 * Web, iOS and Android each already compute the raw signals for their own KPI
 * tiles (budget overrun, category movers vs the 6-month average, savings
 * streak, year-over-year, goal funding, biggest category). What must NOT be
 * re-derived per platform is the *ranking* and the *de-duplication* — those are
 * product rules, and every per-platform copy of a product rule in this codebase
 * has eventually drifted (the category palettes, the bucket-override
 * precedence, the goal-allocation exclusion in `MonthlyTotalsAggregator`).
 * Platforms adapt their data into [Input] and render [Insight]s. Nothing else.
 *
 * Pure, no I/O, no DOM. Same shape as [analytics.MonthlyTotalsAggregator]:
 * small input data classes plus one entry point.
 *
 * ## The rules
 *
 * 1. **Ranked by severity, most actionable first.** Severity is a fixed
 *    per-signal constant (see `SEVERITY_*`) so the order is a product decision,
 *    not an accident of computation order. Ties break on [InsightKind.ordinal]
 *    for a stable, testable sequence.
 * 2. **Never restate the primary.** Each builder checks [Primary] and bows out:
 *    an OVER_BUDGET primary suppresses the budget-overrun insight entirely, a
 *    category the primary already blames is never re-emitted as a mover or as
 *    the biggest category, and goals the primary already lists with amounts and
 *    time-left never come back as "behind".
 * 3. **Never pad.** Every signal has a noise gate (see `MIN_*`). A quiet month
 *    legitimately produces one insight, or none. Returning fewer is correct;
 *    filler is not.
 */
object DashboardInsights {

    // ------------------------------------------------------------------
    // Output
    // ------------------------------------------------------------------

    /** What a given insight is about. At most one insight per kind is emitted. */
    enum class InsightKind {
        /** Realized expenses have blown past the month's budget plan. */
        BUDGET_OVERAGE,

        /** One category is well above (or below) its 6-month rolling average. */
        CATEGORY_MOVER,

        /** A goal is not getting the monthly contribution it needs to land on time. */
        GOAL_BEHIND,

        /** This month's spending vs the same month a year ago. */
        YEAR_OVER_YEAR,

        /** The single category that ate the biggest share of the month. */
        LARGEST_CATEGORY,

        /** Consecutive months that met the savings target. */
        SAVINGS_STREAK,
    }

    /** Drives the renderer's colour role: red / green / plain. */
    enum class InsightTone { NEGATIVE, POSITIVE, NEUTRAL }

    /**
     * Where a renderer should send the user if it attaches a CTA. Deliberately
     * an enum of destinations rather than a platform route string — each
     * platform maps it to its own navigation.
     */
    enum class InsightAction { REVIEW_BUDGET, OPEN_GOAL, OPEN_TRANSACTIONS }

    /**
     * A localizable line: the key plus its already-computed arguments.
     *
     * The pool hands back key + args rather than a finished string so a renderer
     * can re-resolve on a locale switch without recomputing the signals, and so
     * a test can assert the same insight resolves in every locale.
     */
    data class Text(val key: StringKey, val args: List<String> = emptyList()) {
        /** The line in the locale currently set on `i18n.Strings`. */
        fun resolve(): String =
            if (args.isEmpty()) key.tr() else key.tr(*args.toTypedArray())
    }

    /**
     * One thing worth saying. [severity] is comparable across kinds — higher is
     * more urgent. [subject] is the category name or goal id the insight is
     * about, and doubles as the de-duplication handle (two insights never share
     * a subject) and as the CTA's target.
     */
    data class Insight(
        val kind: InsightKind,
        val severity: Int,
        val title: Text,
        val detail: Text,
        val tone: InsightTone,
        val action: InsightAction? = null,
        val subject: String? = null,
    )

    // ------------------------------------------------------------------
    // Ranking — fixed per-signal severities, highest first
    // ------------------------------------------------------------------

    /** Money is already gone past a plan the user set. Most actionable. */
    const val SEVERITY_BUDGET_OVERAGE = 100

    /** A category spiked vs its own baseline — the actionable "what changed". */
    const val SEVERITY_MOVER_UP = 80

    /** A goal will miss its deadline at the current contribution. */
    const val SEVERITY_GOAL_BEHIND = 70

    /** Spending up vs the same month last year — a slow leak, not an emergency. */
    const val SEVERITY_YOY_UP = 60

    /** Where the money went. Informative, rarely urgent. */
    const val SEVERITY_LARGEST_CATEGORY = 40

    /** Good news, worth reinforcing but never ahead of a problem. */
    const val SEVERITY_STREAK = 30

    /** Spending down vs last year. Good news. */
    const val SEVERITY_YOY_DOWN = 25

    /** A category came in well UNDER its baseline. Good news. */
    const val SEVERITY_MOVER_DOWN = 20

    // ------------------------------------------------------------------
    // Noise gates — below these a signal is not worth a line
    // ------------------------------------------------------------------

    /** Dollars over the budget plan before it is worth mentioning. */
    const val MIN_BUDGET_OVERAGE = 25.0

    /** Percent swing vs the 6-month average before a category counts as a mover. */
    const val MIN_MOVER_PERCENT = 25

    /** Dollar swing a mover also has to clear, so a $12 → $20 category stays quiet. */
    const val MIN_MOVER_DELTA = 50.0

    /** Months of history a category needs before its average means anything. */
    const val MIN_MOVER_HISTORY_MONTHS = 2

    /** Monthly funding shortfall before a goal counts as behind. */
    const val MIN_GOAL_SHORTFALL = 25.0

    /** Percent change vs the same month last year before it is worth a line. */
    const val MIN_YOY_PERCENT = 10

    /** Dollar change year-over-year also has to clear this. */
    const val MIN_YOY_DELTA = 100.0

    /** Share of the month's spending a category needs to be called "the biggest". */
    const val MIN_LARGEST_CATEGORY_SHARE = 0.25

    /** Consecutive on-target months before a streak is worth celebrating. */
    const val MIN_STREAK_MONTHS = 2

    /**
     * Secondary slots the Recommendation widget has at its largest tier: the
     * primary insight is #1, so the pool supplies #2 and #3.
     */
    const val DEFAULT_LIMIT = 2

    // ------------------------------------------------------------------
    // Input
    // ------------------------------------------------------------------

    /**
     * Which primary line [computeSavingsRecommendationCardState] produced,
     * flattened to the distinction the de-duplication rules care about.
     * [SavingsRecommendationCardState.SpendLessNudge] splits in two because the
     * surplus-but-nothing-to-allocate case says something completely different
     * from the genuine-overspend case.
     */
    enum class PrimaryKind {
        NO_GOALS,
        EARLY_MONTH,
        /** The displayed month is over and holds no transactions at all. */
        NO_MONTH_DATA,
        EXPENSES_BEFORE_INCOME,
        HAS_ALLOCATIONS,
        /** Income landed, savings > 0, but the optimizer had nothing to fund. */
        SURPLUS_NO_ROOM,
        /** Income landed and savings ≤ 0 — the genuine overspend. */
        OVER_BUDGET,
    }

    /** Map the authoritative card state onto [PrimaryKind]. Never re-decide it. */
    fun primaryKindOf(state: SavingsRecommendationCardState): PrimaryKind = when (state) {
        is SavingsRecommendationCardState.NoGoals -> PrimaryKind.NO_GOALS
        is SavingsRecommendationCardState.EarlyMonth -> PrimaryKind.EARLY_MONTH
        SavingsRecommendationCardState.NoMonthData -> PrimaryKind.NO_MONTH_DATA
        is SavingsRecommendationCardState.ExpensesBeforeIncome -> PrimaryKind.EXPENSES_BEFORE_INCOME
        is SavingsRecommendationCardState.HasAllocations -> PrimaryKind.HAS_ALLOCATIONS
        is SavingsRecommendationCardState.SpendLessNudge ->
            if (state.availableAmount > 0.0) PrimaryKind.SURPLUS_NO_ROOM else PrimaryKind.OVER_BUDGET
    }

    /**
     * What the primary insight already says, so the pool can avoid saying it
     * again. [namedCategories] and [namedGoalIds] are whatever the primary line
     * *names on screen* — not everything it considered.
     */
    data class Primary(
        val kind: PrimaryKind,
        val namedCategories: Set<String> = emptySet(),
        val namedGoalIds: Set<String> = emptySet(),
    )

    /** The month's budget plan vs what actually landed. */
    data class BudgetOverage(val planned: Double, val realized: Double)

    /** One category this month against its own rolling baseline. */
    data class CategoryMover(
        val category: String,
        val thisMonth: Double,
        val sixMonthAverage: Double,
        /** How many of the previous 6 months actually had this category. */
        val monthsOfHistory: Int,
    )

    /** A goal's monthly requirement vs what it is actually getting. */
    data class GoalFunding(
        val goalId: String,
        val name: String,
        val monthlyNeeded: Double,
        /**
         * What the goal ACTUALLY received in the displayed month —
         * `MonthlyTotalsAggregator.goalAllocationsByGoal`, not the amount some
         * optimizer recommends giving it. Web fed it the recommended split, which
         * is empty whenever the month has no surplus, so an over-budget month
         * reported "$0 set aside so far" for a goal the user had funded four
         * times that month.
         */
        val allocatedThisMonth: Double,
        /**
         * The goal is behind ITS OWN plan, from `analytics.computeGoalPace` — the
         * same call the goal card's badge renders. Required before this pool may
         * call a goal "falling behind", so the card cannot say "Ahead of Schedule"
         * while the insight beneath it says the opposite.
         */
        val isBehindPlan: Boolean,
    )

    /** Expenses for the focal month this year vs the same month last year. */
    data class YearOverYear(val thisYear: Double, val lastYear: Double)

    /** The month's biggest spending category and the month's total. */
    data class CategoryAmount(val category: String, val amount: Double, val monthTotal: Double)

    /**
     * Everything the pool needs. Every field is a signal the dashboards already
     * derive for their own tiles — pass those through rather than recomputing.
     *
     * [formatMoney] is required on purpose: currency formatting is a platform
     * concern (and already exists on each one), so the pool borrows the caller's
     * formatter instead of growing a fourth copy of it.
     */
    data class Input(
        val primary: Primary,
        val formatMoney: (Double) -> String,
        /**
         * The displayed month, in the form that belongs inside a sentence
         * (`utils.MonthNames.inSentence`). Every detail line that compares the month
         * against something else names it, because the card is scoped to the
         * dashboard's display month — usually the last COMPLETE one — and "this
         * month" would be describing a month the reader is not looking at.
         */
        val monthLabel: String,
        val budget: BudgetOverage? = null,
        val movers: List<CategoryMover> = emptyList(),
        val goals: List<GoalFunding> = emptyList(),
        val yearOverYear: YearOverYear? = null,
        val largestCategory: CategoryAmount? = null,
        val savingsStreakMonths: Int = 0,
        val savingsTargetPercent: Int = 0,
    )

    // ------------------------------------------------------------------
    // Entry point
    // ------------------------------------------------------------------

    /**
     * The ranked, de-duplicated insights for the secondary slots, at most
     * [limit] of them. May be empty — a quiet month has nothing to add, and an
     * empty list is a better answer than a filler line.
     */
    fun build(input: Input, limit: Int = DEFAULT_LIMIT): List<Insight> {
        if (limit <= 0) return emptyList()

        val candidates = listOfNotNull(
            budgetOverageInsight(input),
            moverInsight(input),
            goalBehindInsight(input),
            yearOverYearInsight(input),
            largestCategoryInsight(input),
            streakInsight(input),
        )

        // Rule 1: severity desc; kind ordinal as the tie-break so the sequence
        // is fully determined (and therefore assertable).
        val ranked = candidates.sortedWith(
            compareByDescending<Insight> { it.severity }.thenBy { it.kind.ordinal }
        )

        // Rule 2, pool-internal half: two insights never share a subject. The
        // biggest category is very often also the biggest mover, and "Groceries
        // is up 40%" followed by "Groceries is your biggest expense" is the same
        // restatement problem, just between two pool entries instead of against
        // the primary.
        val seenSubjects = mutableSetOf<String>()
        val result = mutableListOf<Insight>()
        for (insight in ranked) {
            val subject = insight.subject
            if (subject != null && !seenSubjects.add(subject)) continue
            result.add(insight)
            if (result.size >= limit) break
        }
        return result
    }

    // ------------------------------------------------------------------
    // Individual signals
    // ------------------------------------------------------------------

    private fun budgetOverageInsight(input: Input): Insight? {
        // Rule 2: the OVER_BUDGET primary already prints the shortfall in
        // dollars with a Review-my-budget CTA. A second over-budget line is the
        // textbook restatement this pool must never produce.
        if (input.primary.kind == PrimaryKind.OVER_BUDGET) return null

        val budget = input.budget ?: return null
        if (budget.planned <= 0.0) return null
        val overage = budget.realized - budget.planned
        if (overage < MIN_BUDGET_OVERAGE) return null

        return Insight(
            kind = InsightKind.BUDGET_OVERAGE,
            severity = SEVERITY_BUDGET_OVERAGE,
            title = Text(StringKey.INSIGHT_BUDGET_OVERAGE_TITLE),
            detail = Text(
                StringKey.INSIGHT_BUDGET_OVERAGE_DETAIL,
                listOf(
                    input.formatMoney(overage),
                    input.formatMoney(budget.realized),
                    input.formatMoney(budget.planned),
                ),
            ),
            tone = InsightTone.NEGATIVE,
            action = InsightAction.REVIEW_BUDGET,
        )
    }

    private fun moverInsight(input: Input): Insight? {
        // Rule 2: a category the primary already blames as the driver is off
        // limits, however big its swing.
        val best = input.movers
            .asSequence()
            .filter { it.category !in input.primary.namedCategories }
            .filter { it.monthsOfHistory >= MIN_MOVER_HISTORY_MONTHS && it.sixMonthAverage > 0.0 }
            .map { it to percentChange(it.thisMonth, it.sixMonthAverage) }
            .filter { (mover, pct) ->
                abs(pct) >= MIN_MOVER_PERCENT &&
                    abs(mover.thisMonth - mover.sixMonthAverage) >= MIN_MOVER_DELTA
            }
            .maxByOrNull { (_, pct) -> abs(pct) }
            ?: return null

        val (mover, pct) = best
        val up = pct > 0
        return Insight(
            kind = InsightKind.CATEGORY_MOVER,
            severity = if (up) SEVERITY_MOVER_UP else SEVERITY_MOVER_DOWN,
            title = Text(
                if (up) StringKey.INSIGHT_MOVER_UP_TITLE else StringKey.INSIGHT_MOVER_DOWN_TITLE,
                listOf(mover.category, percentLabel(abs(pct))),
            ),
            detail = Text(
                StringKey.INSIGHT_MOVER_DETAIL,
                listOf(input.formatMoney(mover.thisMonth), input.monthLabel, input.formatMoney(mover.sixMonthAverage)),
            ),
            tone = if (up) InsightTone.NEGATIVE else InsightTone.POSITIVE,
            action = InsightAction.OPEN_TRANSACTIONS,
            subject = mover.category,
        )
    }

    private fun goalBehindInsight(input: Input): Insight? {
        // Rule 2: a NO_GOALS primary is itself an invitation to create a goal —
        // there is nothing to be behind on. Goals the HAS_ALLOCATIONS primary
        // lists (with amount and time-left) are filtered out individually below.
        if (input.primary.kind == PrimaryKind.NO_GOALS) return null

        val worst = input.goals
            .filter { it.goalId !in input.primary.namedGoalIds }
            // The pace status owns "is this goal behind"; this pool only reports it.
            // A goal ahead of its plan that simply had a thin month is not behind.
            .filter { it.isBehindPlan }
            .filter { it.monthlyNeeded - it.allocatedThisMonth >= MIN_GOAL_SHORTFALL }
            .maxByOrNull { it.monthlyNeeded - it.allocatedThisMonth }
            ?: return null

        return Insight(
            kind = InsightKind.GOAL_BEHIND,
            severity = SEVERITY_GOAL_BEHIND,
            title = Text(StringKey.INSIGHT_GOAL_BEHIND_TITLE, listOf(worst.name)),
            detail = Text(
                StringKey.INSIGHT_GOAL_BEHIND_DETAIL,
                listOf(
                    input.formatMoney(worst.monthlyNeeded),
                    input.formatMoney(worst.allocatedThisMonth),
                ),
            ),
            tone = InsightTone.NEGATIVE,
            action = InsightAction.OPEN_GOAL,
            subject = worst.goalId,
        )
    }

    private fun yearOverYearInsight(input: Input): Insight? {
        val yoy = input.yearOverYear ?: return null
        if (yoy.lastYear <= 0.0) return null
        val pct = percentChange(yoy.thisYear, yoy.lastYear)
        if (abs(pct) < MIN_YOY_PERCENT) return null
        if (abs(yoy.thisYear - yoy.lastYear) < MIN_YOY_DELTA) return null

        val up = pct > 0
        return Insight(
            kind = InsightKind.YEAR_OVER_YEAR,
            severity = if (up) SEVERITY_YOY_UP else SEVERITY_YOY_DOWN,
            title = Text(
                if (up) StringKey.INSIGHT_YOY_UP_TITLE else StringKey.INSIGHT_YOY_DOWN_TITLE,
                listOf(percentLabel(abs(pct))),
            ),
            detail = Text(
                StringKey.INSIGHT_YOY_DETAIL,
                listOf(input.formatMoney(yoy.thisYear), input.monthLabel, input.formatMoney(yoy.lastYear)),
            ),
            tone = if (up) InsightTone.NEGATIVE else InsightTone.POSITIVE,
        )
    }

    private fun largestCategoryInsight(input: Input): Insight? {
        val largest = input.largestCategory ?: return null
        // Rule 2: if the primary already names it, saying it again adds nothing.
        if (largest.category in input.primary.namedCategories) return null
        if (largest.amount <= 0.0 || largest.monthTotal <= 0.0) return null
        val share = largest.amount / largest.monthTotal
        if (share < MIN_LARGEST_CATEGORY_SHARE) return null

        return Insight(
            kind = InsightKind.LARGEST_CATEGORY,
            severity = SEVERITY_LARGEST_CATEGORY,
            title = Text(StringKey.INSIGHT_LARGEST_CATEGORY_TITLE, listOf(largest.category)),
            detail = Text(
                StringKey.INSIGHT_LARGEST_CATEGORY_DETAIL,
                listOf(input.formatMoney(largest.amount), percentLabel((share * 100).roundToInt()), input.monthLabel),
            ),
            tone = InsightTone.NEUTRAL,
            action = InsightAction.OPEN_TRANSACTIONS,
            subject = largest.category,
        )
    }

    private fun streakInsight(input: Input): Insight? {
        if (input.savingsStreakMonths < MIN_STREAK_MONTHS) return null
        val months = input.savingsStreakMonths.toString()
        return Insight(
            kind = InsightKind.SAVINGS_STREAK,
            severity = SEVERITY_STREAK,
            title = Text(StringKey.INSIGHT_STREAK_TITLE, listOf(months)),
            detail = Text(
                StringKey.INSIGHT_STREAK_DETAIL,
                listOf(percentLabel(input.savingsTargetPercent), months),
            ),
            tone = InsightTone.POSITIVE,
        )
    }

    // ------------------------------------------------------------------
    // Helpers
    // ------------------------------------------------------------------

    /** Signed percent change of [actual] against [baseline]; 0 when there is no baseline. */
    private fun percentChange(actual: Double, baseline: Double): Int =
        if (baseline <= 0.0) 0 else (((actual - baseline) / baseline) * 100).roundToInt()

    /** "42%" — the same bare form every dashboard tile already prints. */
    private fun percentLabel(pct: Int): String = "$pct%"
}
